import { inject } from '@angular/core';
import type { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { type Observable, catchError, finalize, from, map, of, shareReplay, switchMap, throwError } from 'rxjs';
import { TokenStorageService } from '@/core/storage/token-storage.service';
import { AuthApiService } from '@/core/user/auth-api.service';
import { AuthStore } from '@/features/auth-store.service';
import { environment } from '@environment/environments';
import { APP_URLS } from '@/routes/app.paths';
import { CONFIRM_FORGOT_PASSWORD_URL, FORCE_CHANGE_PASSWORD_URL, FORGOT_PASSWORD_URL, LOGIN_URL } from './auth.config';
import { CONTACT_REQUEST_URL } from '@/core/contact/contact.config';

const PUBLIC_URLS = new Set([
  LOGIN_URL,
  FORGOT_PASSWORD_URL,
  CONFIRM_FORGOT_PASSWORD_URL,
  FORCE_CHANGE_PASSWORD_URL,
  CONTACT_REQUEST_URL,
]);

let refreshInFlight: Observable<string | null> | null = null;

function attach(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.baseUrl) || PUBLIC_URLS.has(req.url)) return next(req);

  const tokenStorage = inject(TokenStorageService);
  const authApi = inject(AuthApiService);
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const accessToken = tokenStorage.getAccessToken();
  const firstReq = accessToken === null || accessToken === '' ? req : attach(req, accessToken);

  const forceLogout = (): Observable<never> => {
    authStore.logout();
    void router.navigate([APP_URLS.login]);
    return throwError(() => new HttpErrorResponse({ status: 401 }));
  };

  const handleUnauthorized = (handler: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken === null || refreshToken === '') return forceLogout();

    refreshInFlight ??= from(authApi.refresh(refreshToken)).pipe(
      map((result) => {
        authStore.storeAuthResult(result);
        return result.accessToken;
      }),
      catchError(() => of(null)),
      finalize(() => {
        refreshInFlight = null;
      }),
      shareReplay(1),
    );

    return refreshInFlight.pipe(
      switchMap((newToken) => {
        if (newToken === null || newToken === '') return forceLogout();
        return handler(attach(req, newToken));
      }),
    );
  };

  return next(firstReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handleUnauthorized(next);
      }
      return throwError(() => error);
    }),
  );
};
